/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { Formik, Field as FormikField } from 'formik'
import { useServiceContext } from '../../../root'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { Select } from '../../../components/Select'
import { EtmfListView, EtmfLevel, type EtmfListViewValue } from '../../../constants/etmf'

interface EtmfListFilterProps {
  listView: EtmfListViewValue
  pageTexts: {
    title: string
    filterLabel: string
    addNewMDF: string
  }
}

export interface EtmfListFilterRef {
  refreshPackageOptions: () => Promise<void>
}

interface MDFFilterValues {
  name: string
}

interface PackagesFilterValues {
  ids: number[]
  level: string
  filePlanId: number | null
}

type FilterValues = MDFFilterValues | PackagesFilterValues

const isMDFFilter = (filter: FilterValues): filter is MDFFilterValues => {
  return Object.prototype.hasOwnProperty.call(filter, 'name')
}

const isPackagesFilter = (filter: FilterValues): filter is PackagesFilterValues => {
  return Object.prototype.hasOwnProperty.call(filter, 'ids') && Object.prototype.hasOwnProperty.call(filter, 'level')
}

export const EtmfListFilter = forwardRef<EtmfListFilterRef, EtmfListFilterProps>(function EtmfListFilter(
  { listView, pageTexts },
  ref
) {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const {
    service: {
      filter: { translate },
      persist
    },
    api
  } = context

  const persistenceKey = tableData.persistenceKey + '_filter'
  const isPackageView = listView === EtmfListView.PACKAGE

  // Default filters
  const defaultMDFFilter: MDFFilterValues = {
    name: ''
  }

  const defaultPackagesFilter: PackagesFilterValues = {
    ids: [],
    level: EtmfLevel.ALL,
    filePlanId: null
  }

  const [filter, setFilter] = useState<FilterValues | null>(null)
  const [packageOptions, setPackageOptions] = useState<Array<{ value: number; label: string }>>([])
  const [filePlanOptions, setFilePlanOptions] = useState<Array<{ value: number; label: string }>>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false)

  // Function to load package and file plan options
  const loadPackageOptions = async () => {
    if (!isPackageView) return

    setIsLoadingOptions(true)
    try {
      const [packagesData, filePlansData] = await Promise.all([
        api.packages.getAllPackagesForPicker(),
        api.etmf.getFilePlansForPicker()
      ])

      setPackageOptions(
        packagesData.elements.map((pkg: { id: number | string; name: string }) => ({
          value: Number(pkg.id),
          label: pkg.name
        }))
      )
      setFilePlanOptions(
        filePlansData.elements.map((fp: { id: number; name: string }) => ({
          value: fp.id,
          label: fp.name
        }))
      )
    } catch (error: unknown) {
      console.error('Failed to load package/file plan options:', error)
    } finally {
      setIsLoadingOptions(false)
    }
  }

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refreshPackageOptions: async () => {
      await loadPackageOptions()
      tableData.refreshTable()
    }
  }))

  useEffect(() => {
    // Load persisted filter or use default
    const persistedFilter = persist.getBasic<FilterValues>(persistenceKey)
    const newFilter = persistedFilter || (isPackageView ? defaultPackagesFilter : defaultMDFFilter)

    setFilter(newFilter)
    doFilter(newFilter)

    // Load options for package view
    if (isPackageView) {
      loadPackageOptions()
    }
  }, [listView, persistenceKey, isPackageView])

  const doFilter = (values: FilterValues) => {
    // Only persist and apply the filter via tableData.setFilter.
    // RcmTableDataWrapper has a useEffect on filter state that automatically
    // calls refreshTable when filter changes — do NOT call refreshTable manually
    // here, as setFilter is async (React state) and refreshTable would read the
    // stale (old) filter value from the closure, causing a duplicate API call
    // with wrong data.
    persist.setBasic(persistenceKey, values)
    tableData.setFilter({ ...values })
  }

  const handleReset = (resetForm: () => void) => {
    const defaultFilter = isPackageView ? { ...defaultPackagesFilter } : { ...defaultMDFFilter }
    doFilter(defaultFilter)
    resetForm()
  }

  const isFilterModified = (values: FilterValues): boolean => {
    if (isPackageView && isPackagesFilter(values)) {
      const defaults = defaultPackagesFilter
      return (
        values.ids.length !== defaults.ids.length ||
        values.level !== defaults.level ||
        values.filePlanId !== defaults.filePlanId
      )
    } else if (isMDFFilter(values)) {
      return values.name !== defaultMDFFilter.name
    }
    return false
  }

  if (tableData.isFilterCollapsed || !filter) {
    return null
  }

  return (
    <div className="filter-wrapper">
      <Formik
        initialValues={filter}
        enableReinitialize={true}
        onSubmit={(values: FilterValues) => {
          doFilter(values)
        }}
      >
        {({ values, handleSubmit, setFieldValue, resetForm }) => (
          <>
            {isPackageView && isPackagesFilter(values) ? (
              // Packages filter
              <div className="row">
                <div className="col-xs-4">
                  <div className="form-group" id="d674038e-1d5e-42bf-a509-776b48da4b3e">
                    <label style={{ fontSize: '14px' }}>{translate('PACKAGE_NAME_LABEL')}</label>
                    <Select
                      placeholder={translate('SELECT_PACKAGES_PLACEHOLDER')}
                      name="ids"
                      value={values.ids}
                      options={packageOptions}
                      onChange={(option: any) => {
                        setFieldValue('ids', option ? option.map((o: any) => o.value) : [])
                      }}
                      isMulti={true}
                      searchable
                      isLoading={isLoadingOptions}
                      id="a030c4ad-b4ae-4e4c-b86c-110a237cb24a"
                    />
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group">
                    <label style={{ fontSize: '14px' }}>{translate('EMTF_LEVEL')}</label>
                    {Object.keys(EtmfLevel).map((levelKey) => {
                      const levelValue = EtmfLevel[levelKey as keyof typeof EtmfLevel]
                      return (
                        <div key={levelKey} className="radio classic">
                          <input
                            type="radio"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue('level', event.target.value)
                            }}
                            value={levelValue}
                            id={`7f3c6b63-31e1-4b8e-9679-06e225301faa-${levelValue}`}
                            name="level"
                            checked={values.level === levelValue}
                          />
                          <label htmlFor={`7f3c6b63-31e1-4b8e-9679-06e225301faa-${levelValue}`}>
                            {translate('EMTF_MD_' + levelKey)}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="col-xs-4">
                  <div className="form-group" id="d674038e-1d5e-42bf-a509-776b48da4b3f">
                    <label style={{ fontSize: '14px' }}>{translate('FILE_PLAN')}</label>
                    <Select
                      placeholder={translate('SELECT_FILE_PLAN_PLACEHOLDER')}
                      name="filePlanId"
                      value={values.filePlanId}
                      options={filePlanOptions}
                      onChange={(option: any) => {
                        setFieldValue('filePlanId', option ? option.value : null)
                      }}
                      searchable
                      isLoading={isLoadingOptions}
                      id="a030c4ad-b4ae-4e4c-b86c-110a237cb24b"
                    />
                  </div>
                </div>
              </div>
            ) : (
              // MDF/FilePlans filter
              <div className="row">
                <div className="col-xs-12">
                  <div className="form-group w50p" id="d674038e-1d5e-42bf-a509-776b48da4b3e">
                    <label>{translate(pageTexts.filterLabel)}</label>
                    <FormikField
                      type="text"
                      name="name"
                      id="d674038e-1d5e-42bf-a509-776b48da4b3e"
                      className="form-control field-control"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="clearfix">
              <div className="pull-left">
                <button
                  type="button"
                  data-action={'filter'}
                  disabled={tableData.isLoading}
                  className="btn btn-primary"
                  onClick={() => handleSubmit()}
                  id="a7728b4c-476f-4fb9-a6af-6982156e5153"
                >
                  {translate('BTN_FILTER')}
                </button>
                <button
                  type="button"
                  data-action={'reset'}
                  disabled={!isFilterModified(values)}
                  className="btn btn-chita btn-link at-reset"
                  onClick={() => handleReset(resetForm)}
                  id="131d6033-ee66-49c7-bfb4-21d65bc84ecc"
                >
                  {translate('BTN_CLEAR_FILTER')}
                </button>
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
})
